/*
 * Inbox item model
 * Purpose: Share the API-backed AI Inbox row shape between services, pages, and tests.
 * Implementation notes: Mirrors dbo.Inbox fields exposed by /api/data/inbox with nullable provenance and trace fields.
 * Recent changes: Added durable actionTitle support while keeping legacy parsing as a fallback.
 */

export type InboxStatus = 'open' | 'done' | 'dismissed' | 'removed' | 'superseded' | 'no_longer_supported';
export type InboxActionCategory = 'retain' | 'recruit' | 'escalate' | 'clarify' | 'support' | 'monitor' | 'source correction' | 'legal/commercial review' | 'relationship owner review';

export interface InboxItem {
  inboxId: number;
  teamId: number;
  teamName: string | null;
  accountId: number | null;
  accountName: string | null;
  contactId: number | null;
  contactName: string | null;
  sourceDate: string | null;
  actionTitle: string | null;
  actionText: string;
  displayActionText: string;
  actionCategory: InboxActionCategory | null;
  actionCategoryLabel: string | null;
  status: InboxStatus;
  firstSeenDate: string | null;
  lastSeenDate: string | null;
  sourcePath: string | null;
  sourceActionsReportPath: string | null;
  sourceSummaryPath: string | null;
  sourceMarkdownSection: string | null;
  traceMarkdown: string | null;
  insightText: string | null;
  tensionsText: string | null;
  memoryText: string | null;
  evidenceDate: string | null;
  removedDate: string | null;
  removalReason: string | null;
  actionKey: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export const INBOX_STATUSES: InboxStatus[] = [
  'open',
  'done',
  'dismissed',
  'removed',
  'superseded',
  'no_longer_supported'
];

export const INBOX_STATUS_LABELS: Record<InboxStatus, string> = {
  open: 'Open',
  done: 'Done',
  dismissed: 'Dismissed',
  removed: 'Removed',
  superseded: 'Superseded',
  no_longer_supported: 'No longer supported'
};

export const INBOX_ACTION_CATEGORIES: InboxActionCategory[] = [
  'retain',
  'recruit',
  'escalate',
  'clarify',
  'support',
  'monitor',
  'source correction',
  'legal/commercial review',
  'relationship owner review'
];

export const INBOX_ACTION_CATEGORY_LABELS: Record<InboxActionCategory, string> = {
  retain: 'Retain',
  recruit: 'Recruit',
  escalate: 'Escalate',
  clarify: 'Clarify',
  support: 'Support',
  monitor: 'Monitor',
  'source correction': 'Source correction',
  'legal/commercial review': 'Legal/commercial review',
  'relationship owner review': 'Relationship owner review'
};
